// src/lib/auth-context.tsx
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import type { Session, User } from '@supabase/supabase-js'
import { supabase } from '@/supabase/client'

const CORE_API = import.meta.env.VITE_CORE_API_URL || ''
const ROLE_STORAGE_KEY = 'project-tower:last-role'

type AuthStatus = 'idle' | 'checking' | 'ready' | 'error'

type AuthProfile = {
  role?: string | null
  // TODO: Narrow profile shape as API responses are standardized.
  [key: string]: unknown
}

type AuthSessionResponse = {
  profile?: AuthProfile | null
  [key: string]: unknown
}

type AuthContextValue = {
  session: Session | null
  user: User | null
  role: string | null
  isAdmin: boolean
  previewRole: string | null
  setPreviewRole: (role: string | null) => void
  isSuperAdmin: boolean
  profile: AuthProfile | null
  status: AuthStatus
  error: string | null
  initializing: boolean
  profileLoading: boolean
  profileError: string | null
  signOut: () => Promise<void>
  fetchProfile: (currentSession?: Session | null) => Promise<AuthSessionResponse | null>
  rememberRole: (role: string) => void
  refresh: () => Promise<AuthSessionResponse | null>
  offlineMode: boolean
  setOfflineMode: (value: boolean) => void
}

type ErrorWithStatus = Error & { status?: number }

const AuthContext = createContext<AuthContextValue | null>(null)

type AuthProviderProps = {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<AuthProfile | null>(null)
  const [previewRole, setPreviewRole] = useState<string | null>(null)
  const [status, setStatus] = useState<AuthStatus>('idle')
  const [error, setError] = useState<string | null>(null)
  const [offlineMode, setOfflineMode] = useState(false)

  const enablePasskeys = import.meta.env?.VITE_ENABLE_PASSKEYS === 'true'
  const passkeyResumeAttempted = useRef(false)
  const profileSessionTokenRef = useRef<string | null>(null)
  const fetchCallIdRef = useRef(0)

  // --- Session bootstrap & listener -----------------------------------------
  useEffect(() => {
    let isMounted = true

    const loadSession = async () => {
      setStatus('checking')
      try {
        const { data } = await supabase.auth.getSession()
        if (!isMounted) return
        const nextSession = data?.session ?? null
        setSession(nextSession)
        setOfflineMode(false)
        if (!nextSession) {
          setProfile(null)
          profileSessionTokenRef.current = null
          setError(null)
          setStatus('ready')
        }
      } catch (loadError) {
        if (!isMounted) return
        const message =
          (loadError as Error | { message?: string } | null | undefined)?.message || 'Failed to load session'
        setError(message)
        setStatus('error')
      }
    }

    loadSession()

    const { data } = supabase.auth.onAuthStateChange((_event, newSession) => {
      if (!isMounted) return
      setSession(newSession ?? null)
      if (!newSession) {
        setProfile(null)
        profileSessionTokenRef.current = null
        setError(null)
        setStatus('ready')
      } else {
        setStatus('checking')
      }
    })

    return () => {
      isMounted = false
      data?.subscription?.unsubscribe?.()
    }
  }, [])

  // --- Helpers declared BEFORE effects that depend on them -------------------
  const rememberRole = useCallback((role: string) => {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.setItem(ROLE_STORAGE_KEY, role)
    } catch {
      /* ignore storage errors */
    }
  }, [])

  const signOut = useCallback(async () => {
    await supabase.auth.signOut()
    setSession(null)
    setProfile(null)
    profileSessionTokenRef.current = null
    setError(null)
    setStatus('ready')
  }, [])

  const fetchProfile = useCallback(
    async (currentSession?: Session | null) => {
      const activeSession = currentSession ?? session
      if (!activeSession?.access_token) {
        setProfile(null)
        profileSessionTokenRef.current = null
        setError(null)
        setStatus('ready')
        return null
      }

      const targetToken = activeSession.access_token
      const fetchId = fetchCallIdRef.current + 1
      fetchCallIdRef.current = fetchId

      if (profileSessionTokenRef.current && profileSessionTokenRef.current !== targetToken) {
        setProfile(null)
        profileSessionTokenRef.current = null
      }

      setStatus('checking')
      setError(null)
      try {
        const response = await fetch('/.netlify/functions/auth-session', {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${activeSession.access_token}`,
          },
        })

        if (!response.ok) {
          let message = 'Unable to load profile'
          try {
            const errorData = (await response.json()) as { error?: string }
            message = errorData?.error || message
          } catch (parseError) {
            try {
              const text = await response.text()
              if (text) message = text
            } catch {
              /* ignore */
            }
          }
          const err = new Error(message || 'Unable to load profile') as ErrorWithStatus
          err.status = response.status
          throw err
        }

        const data = (await response.json()) as AuthSessionResponse
        if (fetchId !== fetchCallIdRef.current) {
          return data
        }

        profileSessionTokenRef.current = targetToken
        setProfile(data?.profile ?? null)
        setError(null)
        setStatus('ready')
        setOfflineMode(false)
        return data
      } catch (fetchError) {
        const errorInstance = fetchError as ErrorWithStatus
        const message = errorInstance?.message || 'Unable to load profile'
        console.warn('auth-session soft-failed:', fetchError)
        const shouldEnableOffline = errorInstance?.status == null || (errorInstance.status ?? 0) >= 500
        if (shouldEnableOffline) {
          setOfflineMode(true)
          try {
            const { data: fallbackData } = await supabase.auth.getSession()
            if (fallbackData?.session && fetchId === fetchCallIdRef.current) {
              setSession(fallbackData.session)

              // Fallback: load profile directly from Supabase when Render is unreachable
              const userId = fallbackData.session.user?.id
              if (userId) {
                const { data: fallbackProfile } = await supabase
                  .from('profiles')
                  .select('id, email, role, full_name')
                  .eq('id', userId)
                  .maybeSingle()

                if (fallbackProfile?.role && fetchId === fetchCallIdRef.current) {
                  profileSessionTokenRef.current = targetToken
                  setProfile(fallbackProfile)
                  setError(null)
                  setStatus('ready')
                  return { profile: fallbackProfile }
                }
              }
            }
          } catch (fallbackError) {
            console.warn('Supabase session/profile fallback failed', fallbackError)
          }
        }
        if (fetchId === fetchCallIdRef.current) {
          setProfile(null)
          profileSessionTokenRef.current = null
          setError(message)
          setStatus('ready')
        }
        return null
      }
    },
    [session],
  )

  const refresh = useCallback(async () => {
    if (!session?.access_token) {
      setProfile(null)
      profileSessionTokenRef.current = null
      setError(null)
      setStatus('ready')
      return null
    }

    return fetchProfile(session)
  }, [fetchProfile, session])

  // --- Hydrate/ensure profile when session changes --------------------------
  useEffect(() => {
    if (!session?.access_token) {
      setProfile(null)
      profileSessionTokenRef.current = null
      setError(null)
      setStatus('ready')
      return
    }

    let isActive = true

    const hydrateProfile = async () => {
      try {
        await fetchProfile(session)
        if (!isActive) return
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem(ROLE_STORAGE_KEY)
        }
      } catch (loadError) {
        if (!isActive) return
        console.warn('Failed to load profile', loadError)
      }
    }

    hydrateProfile()
    return () => {
      isActive = false
    }
  }, [fetchProfile, session])

  // Reset passkey attempt state when we actually have a session
  useEffect(() => {
    if (session?.access_token) {
      passkeyResumeAttempted.current = false
    }
  }, [session])

  // --- Passkey session resume (only if enabled) -----------------------------
  useEffect(() => {
    if (!enablePasskeys) return
    if (passkeyResumeAttempted.current) return
    if (status === 'checking') return
    if (session?.access_token) return
    if (typeof document === 'undefined') return
    if (!document.cookie?.includes('pt_session=')) return

    passkeyResumeAttempted.current = true
    let cancelled = false

    const resumeFromPasskey = async () => {
      try {
        const response = await fetch(`${CORE_API}/api/v1/auth/webauthn/session`, {
          method: 'POST',
          credentials: 'include',
        })

        if (!response.ok) {
          passkeyResumeAttempted.current = false
          return
        }

        const data = (await response.json()) as { email?: string; otp?: string }
        if (!data?.email || !data?.otp || cancelled) {
          passkeyResumeAttempted.current = false
          return
        }

        const { data: otpData, error: otpError } = await supabase.auth.verifyOtp({
          email: data.email,
          token: data.otp,
          type: 'magiclink',
        })

        if (otpError || !otpData?.session || cancelled) {
          passkeyResumeAttempted.current = false
          return
        }

        const activeSession = otpData.session
        rememberRole('sponsor')

        // Best-effort profile refresh after passkey session resumes
        try {
          await fetchProfile(activeSession)
        } catch (profileError) {
          console.warn('Passkey resume profile error', profileError)
        }
      } catch (resumeError) {
        console.warn('Passkey resume error', resumeError)
        passkeyResumeAttempted.current = false
      }
    }

    resumeFromPasskey()
    return () => {
      cancelled = true
    }
  }, [enablePasskeys, fetchProfile, rememberRole, session, status])

  // --- Context value ---------------------------------------------------------
  const role = profile?.role ?? null
  const user = session?.user ?? null
  const checking = status === 'checking'
  const isAdmin = ['admin', 'superadmin'].includes(role ?? '')
  const isSuperAdmin = role === 'superadmin'

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user,
      role,
      isAdmin,
      previewRole,
      setPreviewRole,
      isSuperAdmin,
      profile,
      status,
      error,
      initializing: checking,
      profileLoading: checking,
      profileError: error,
      signOut,
      fetchProfile,
      rememberRole,
      refresh,
      offlineMode,
      setOfflineMode,
    }),
    [
      fetchProfile,
      checking,
      error,
      isAdmin,
      isSuperAdmin,
      offlineMode,
      profile,
      rememberRole,
      refresh,
      role,
      previewRole,
      session,
      status,
      signOut,
      user,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within an AuthProvider')
  return context
}

export const useAuthContext = () => useAuth()
